import { createSelector } from 'reselect';
import { fromJS, Map as IMap, Set as ISet } from 'immutable';

import { FEATURES, createSelectorWhenEnabled } from '../utils/featureFlag';
import { getCurrencyCodes, getPaidCardsCount } from '../item/itemSelectors';
import { getBrandCatalogs, getBrandElectronicCurrencyList, getBrandPlasticCurrencyList } from '../brand/brandSelectors';
import { getIntlLocale, messages, messagesForProgram } from '../intl/intlSelectors';
import { getPreferredCurrencyCode } from '../routing/flowSelectors';

import {
    getProgramElectronicCurrencyList,
    getProgramPlasticCurrencyList,
    getProgramSelectorList,
    getPublishedProgramsList,
    isProgramSelectionEnabled
} from './programSelectors';
import { optionalProgramMessageIds } from './programMessages';


export const DEFAULT_PHYSICAL_FACEPLATES = 'defaultPhysicalFaceplates';
export const DEFAULT_ELECTRONIC_FACEPLATES = 'defaultElectronicFaceplates';


export const getSelectableProgramsList = createSelector(
    getPublishedProgramsList,
    getProgramSelectorList,
    getCurrencyCodes,
    getBrandCatalogs,
    getPreferredCurrencyCode,
    (publishedProgramsList, programSelectorList, cartCurrencyCodes, brandCatalogs, preferredCurrencyCode) => {
        
        const publishedListWithDefault = publishedProgramsList.push(fromJS({ programCode: '' }));
        let selectablePrograms = [];
        programSelectorList.forEach((selectorProgram) => {
            const selectableProgram = publishedListWithDefault.find(
                publishedProgram => publishedProgram.get('programCode') === selectorProgram.get('programCode')
            );
            if (selectableProgram) {
                selectablePrograms.push(selectableProgram);
            }
        });
        
        
        
        
        
        if (preferredCurrencyCode && cartCurrencyCodes.isEmpty()) {
            selectablePrograms = selectablePrograms.filter((program) => {
                const isProgram = program.has('catalogs');
                return isProgram ? program.getIn(['catalogs', preferredCurrencyCode])
                    : brandCatalogs.get(preferredCurrencyCode);
            });
        }

        
        if (cartCurrencyCodes.size > 0) {
            selectablePrograms = selectablePrograms.filter((program) => {
                const isProgram = program.has('catalogs');
                return isProgram ? program.getIn(['catalogs', cartCurrencyCodes.first()])
                    : brandCatalogs.get(cartCurrencyCodes.first());
            });
        }

        return fromJS(selectablePrograms);
    }
);


export const getShowProgramSelection = createSelectorWhenEnabled(FEATURES.PROGRAM_PICKER, false)(
    isProgramSelectionEnabled,
    getSelectableProgramsList,
    (isEnabled, programList) => {
        
        if (!isEnabled) {
            return false;
        }
        
        if (programList.isEmpty()) {
            return false;
        }
        return true;
    }
);


export const getClassicCardDescription = createSelector(
    messages,
    msgs => msgs.get(optionalProgramMessageIds.classicCardDescription, '')
);


export const getCustomCardMessages = createSelector(
    messages,
    msgs => (
        fromJS({
            customCardHeader: msgs.get(optionalProgramMessageIds.customCardHeader, ''),
            customCardTitle: msgs.get(optionalProgramMessageIds.customCardTitle, ''),
            customCardDescription: msgs.get(optionalProgramMessageIds.customCardDescription, ''),
            customCardImage: msgs.get(optionalProgramMessageIds.customCardImage, ''),
            customCardActionButton: msgs.get(optionalProgramMessageIds.customCardActionButton, ''),
            customCardActionUrl: msgs.get(optionalProgramMessageIds.customCardActionUrl, ''),
            customCardImageAlt: msgs.get(optionalProgramMessageIds.customCardImageAlt, '')
        })
    )
);


export const getAlternateImageForProgram = createSelector(
    messagesForProgram,
    programMessages => (programCode) => {
        
        
        
        
        
        
        const imageMessageId = programCode
            ? `${programCode}.${optionalProgramMessageIds.pickerPreviewImage}`
            : optionalProgramMessageIds.pickerPreviewImage;
        const altTextMessageId = programCode
            ? `${programCode}.${optionalProgramMessageIds.pickerPreviewImageAlt}`
            : optionalProgramMessageIds.pickerPreviewImageAlt;
        return fromJS({
            previewImageFilename: programMessages(programCode).get(imageMessageId),
            previewImageAltText: programMessages(programCode).get(altTextMessageId)
        });
    }
);


export const getDefaultFaceplateForProgram = createSelector(
    getProgramSelectorList,
    getIntlLocale,
    (programList, locale) => (programCode, faceplateType) => {
        
        
        const programConfig = programList.find(program => program.get('programCode') === (programCode || ''));
        if (!programConfig) {
            return IMap();
        }
        return programConfig.getIn([faceplateType, locale], IMap());
    }
);


export const getAllAvailableCurrencies = createSelector(
    getBrandPlasticCurrencyList,
    getBrandElectronicCurrencyList,
    getProgramElectronicCurrencyList,
    getProgramPlasticCurrencyList,
    getPublishedProgramsList,
    (
        plasticCurrencies,
        electronicCurrencies,
        programElectronicCurrencyList,
        programPhysicalCurrencyList,
        programList
    ) => {
        const programCurrencies = programList.flatMap((program) => {
            const programCode = program.get('programCode');
            return programPhysicalCurrencyList(programCode).concat(programElectronicCurrencyList(programCode));
        });
        return ISet(plasticCurrencies).union(electronicCurrencies).union(programCurrencies);
    }
);


export const getShowCurrencyCodeOnProgramTiles = createSelector(
    getAllAvailableCurrencies,
    getPaidCardsCount,
    (allAvailableCurrencies, paidCardsCount) => (
        paidCardsCount === 0 && allAvailableCurrencies.size > 1
    )
);


export const getShowCurrencyListOnProgramTiles = createSelector(
    getAllAvailableCurrencies,
    getPaidCardsCount,
    (allAvailableCurrencies, paidCardsCount) => (
        paidCardsCount === 0 && allAvailableCurrencies.size > 1
    )
);
